import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingCart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Cart() {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchCart(); }, []);

  const updateQty = async (productId, qty) => {
    try {
      await axios.put(`${API}/cart/update`, { product_id: productId, quantity: qty }, { withCredentials: true });
      fetchCart();
    } catch { toast.error('Failed to update'); }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${API}/cart/remove/${productId}`, { withCredentials: true });
      toast.success('Removed from cart');
      fetchCart();
    } catch { toast.error('Failed to remove'); }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`, { withCredentials: true });
      toast.success('Cart cleared');
      setCart({ items: [], total: 0 });
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-cream"><div className="spinner"></div></div>;

  return (
    <div className="min-h-screen bg-cream" data-testid="cart-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <h1 className="font-heading text-3xl font-bold text-dark-brown mb-8" data-testid="cart-title">Shopping Cart</h1>

        {cart.items.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingCart className="w-16 h-16 text-border-custom mx-auto mb-4" />
            <p className="font-body text-lg text-light-brown mb-4">Your cart is empty</p>
            <Button onClick={() => navigate('/products')} className="bg-rust hover:bg-rust/90 text-cream font-body rounded-full px-8">
              Continue Shopping
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map(item => (
                <div key={item.product_id} className="flex gap-4 bg-white rounded-xl border border-border-custom p-4" data-testid={`cart-item-${item.product_id}`}>
                  <Link to={`/products/${item.product_id}`} className="shrink-0">
                    <img src={item.product?.image_url} alt={item.product?.name} className="w-24 h-24 object-cover rounded-lg"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=200&h=200&fit=crop'; }} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${item.product_id}`}>
                      <h3 className="font-body font-medium text-dark-brown hover:text-rust transition-colors truncate">{item.product?.name}</h3>
                    </Link>
                    <p className="font-body text-xs text-light-brown">{item.product?.category}</p>
                    <span className="font-body font-bold text-rust" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{'\u20B9'}{item.product?.price?.toLocaleString('en-IN')}</span>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-border-custom rounded-full">
                        <button onClick={() => updateQty(item.product_id, Math.max(1, item.quantity - 1))} className="px-2 py-1"><Minus className="w-3 h-3" /></button>
                        <span className="px-2 text-sm font-body">{item.quantity}</span>
                        <button onClick={() => updateQty(item.product_id, item.quantity + 1)} className="px-2 py-1"><Plus className="w-3 h-3" /></button>
                      </div>
                      <button onClick={() => removeItem(item.product_id)} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`remove-item-${item.product_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="font-body font-bold text-dark-brown text-right" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                    {'\u20B9'}{((item.product?.price || 0) * item.quantity).toLocaleString('en-IN')}
                  </div>
                </div>
              ))}
              <button onClick={clearCart} className="text-sm text-red-500 hover:underline font-body" data-testid="clear-cart-btn">Clear Cart</button>
            </div>

            {/* Summary */}
            <div className="bg-white rounded-xl border border-border-custom p-6 h-fit sticky top-24">
              <h3 className="font-heading text-xl font-bold text-dark-brown mb-4">Order Summary</h3>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between font-body text-sm">
                  <span className="text-light-brown">Subtotal ({cart.items.reduce((s, i) => s + i.quantity, 0)} items)</span>
                  <span className="text-dark-brown font-medium">{'\u20B9'}{cart.total.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between font-body text-sm">
                  <span className="text-light-brown">Shipping</span>
                  <span className="text-green-600 font-medium">Free</span>
                </div>
                <div className="border-t border-border-custom pt-3 mt-1 flex justify-between">
                  <span className="font-body font-semibold text-dark-brown">Total to Pay</span>
                  <span className="font-body text-xl font-bold text-rust" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }} data-testid="cart-total">{'\u20B9'}{cart.total.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              </div>
              <Button onClick={() => navigate('/checkout')} className="w-full bg-rust hover:bg-rust/90 text-cream font-body rounded-full h-12" data-testid="proceed-checkout-btn">
                Proceed to Checkout <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link to="/products" className="block text-center mt-3 font-body text-sm text-rust hover:underline">Continue Shopping</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
